import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import {
  HiHome,
  HiKey,
  HiUsers,
  HiUserGroup,
  HiOfficeBuilding,
  HiGlobeAlt,
  HiCog,
  HiDocumentText,
  HiCollection,
} from 'react-icons/hi';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { getUnreadRequestCount } from '../../services/requests.api';
import type { Role } from '../../types';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: Role[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: HiHome, roles: ['ADMIN', 'OWNER', 'PARTNER', 'INVOICE_TEAM', 'CUSTOMER'] },
  { name: 'Licenses', href: '/licenses', icon: HiKey, roles: ['ADMIN', 'OWNER', 'INVOICE_TEAM', 'CUSTOMER'] },
  { name: 'Customers', href: '/customers', icon: HiUsers, roles: ['ADMIN', 'OWNER', 'INVOICE_TEAM', 'PARTNER'] },
  { name: 'Owners', href: '/owners', icon: HiGlobeAlt, roles: ['ADMIN'] },
  { name: 'Partners', href: '/partners', icon: HiOfficeBuilding, roles: ['ADMIN', 'OWNER', 'INVOICE_TEAM'] },
  { name: 'Products', href: '/products', icon: HiCollection, roles: ['ADMIN', 'OWNER', 'INVOICE_TEAM'] },
  { name: 'Users', href: '/users', icon: HiUserGroup, roles: ['ADMIN'] },
  { name: 'Requests', href: '/requests', icon: HiDocumentText, roles: ['ADMIN', 'OWNER', 'INVOICE_TEAM', 'PARTNER', 'CUSTOMER'] },
  { name: 'Settings', href: '/settings', icon: HiCog, roles: ['ADMIN'] },
];

export function Sidebar() {
  const user = useAuthStore((state) => state.user);
  const lastSeenMap = useNotificationStore((state) => state.requestsLastSeen);
  const markRequestsSeen = useNotificationStore((state) => state.markRequestsSeen);

  // Initialise "last seen" to now on first ever load so the badge doesn't show the whole backlog.
  useEffect(() => {
    if (user && !lastSeenMap[user.id]) {
      markRequestsSeen(user.id);
    }
  }, [user, lastSeenMap, markRequestsSeen]);

  const since = user ? lastSeenMap[user.id] : undefined;

  const { data: unreadRequests } = useQuery({
    queryKey: ['requestsUnread', user?.id, since],
    queryFn: () => getUnreadRequestCount(since!),
    enabled: !!user && !!since,
    refetchInterval: 30000,
  });

  const filteredNavigation = navigation.filter(
    (item) => user && item.roles.includes(user.role)
  );

  return (
    <div className="flex flex-col w-64 bg-white border-r border-gray-200">
      <div className="flex items-center h-16 px-6 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
            <HiKey className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-semibold text-gray-900">License Server</span>
        </div>
      </div>

      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {filteredNavigation.map((item) => {
          const badge = item.href === '/requests' ? unreadRequests ?? 0 : 0;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.name}</span>
              {badge > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-white bg-primary-600 rounded-full">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-gray-200">
        <NavLink
          to="/profile"
          title="Profil bearbeiten"
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 px-2 py-2 -mx-2 rounded-lg transition-colors',
              isActive ? 'bg-primary-50' : 'hover:bg-gray-50'
            )
          }
        >
          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-gray-600">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </NavLink>
      </div>
    </div>
  );
}
